import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { ScreeningQuestion } from './Feedback/ScreeningQuestion';
import { SoftSkillAssessment } from './Feedback/SoftSkillAssessment';
import { getInterview } from '../redux/action';
import { FinalRemarks, Footer, Header, OverallSkills, Profile } from './Feedback';
import FullPageProgress from './FullPageProgress';
import { capitalizeAll } from '../utils/helper';
import '../assets/css/feedback.css';
import '../assets/fonts/stylesheet.css';

const Other = React.lazy(() => import('./Pages/Other'));

const sanitizeText = (text) =>
    text
        ?.replace(/\\r/g, '')
        .replace(/\\n/g, '')
        .replace(/\\t/g, '&nbsp;&nbsp;&nbsp;&nbsp;')
        .replace(/\\s/g, '&nbsp;')
        .replace('<br /><br />', '') || '';

function FeedbackRoom() {
    const { roomId, interviewId } = useParams();
    const dispatch = useDispatch();
    const { loading, interview } = useSelector((state) => state.Interview);

    const [, setWidth] = useState(window.innerWidth);

    const [profileForm, setProfileForm] = useState({
        image: '',
        code: '',
        name: '',
        position: '',
        email: '',
        phone: '',
        interviewDate: '',
        interviewDuration: '',
        finalRating: 0,
    });

    const [feedbackForm, setFeedbackForm] = useState({
        resumeSummary: '',
        questions: { status: false, data: [] },
        finalRemarks: { status: false, data: '' },
        softSkillAssessment: null,
    });

    useEffect(() => {
        if (profileForm.name) {
            document.title = `${capitalizeAll(profileForm.name)} Feedback Report`;
        }
    }, [profileForm.name]);

    useEffect(() => {
        if (!roomId || !interviewId) return;

        if (!interview) {
            dispatch(getInterview({ roomId, interviewId }));
        } else {
            const { feedback, code, user, interviewStartDate, position, interviewDuration, score } = interview;

            setProfileForm({
                image: interview?.candidateImage || '',
                code: code || '',
                name: user?.fullName || '',
                email: user?.email || '',
                phone: user?.whatsappNumber || '',
                interviewDate: interviewStartDate || '',
                position: position || '',
                interviewDuration: interviewDuration || '',
                finalRating: score ?? 0,
            });

            if (feedback) {
                setFeedbackForm({
                    resumeSummary: sanitizeText(feedback.resumeSummary),
                    questions: {
                        status: Boolean(feedback.questions?.length),
                        data: feedback.questions || [],
                    },
                    finalRemarks: {
                        status: Boolean(feedback.remark),
                        data:
                            feedback.remark ||
                            'Unfortunately, we couldn’t generate final remarks as not all the questions were answered.',
                    },
                    softSkillAssessment: [
                        { title: 'Fluency', value: feedback?.interviewAssessment?.fluency || 0 },
                        { title: 'Clarity', value: feedback?.interviewAssessment?.clarity || 0 },
                        { title: 'Grammar', value: feedback?.interviewAssessment?.grammar || 0 },
                        { title: 'Confidence', value: feedback?.interviewAssessment?.confidence || 0 },
                    ],
                });
            }
        }
    }, [dispatch, roomId, interviewId, interview]);

    useEffect(() => {
        const handleResize = () => setWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    if (loading) return <FullPageProgress />;

    return (
        <div className="App">
            {interview?.feedback ? (
                <>
                    <Header />
                    <Profile data={profileForm} />
                    <OverallSkills data={feedbackForm} version={feedbackForm?.version} />
                    <SoftSkillAssessment data={feedbackForm?.softSkillAssessment} />
                    {feedbackForm.finalRemarks.status && (
                        <FinalRemarks data={feedbackForm.finalRemarks.data} finalRating={profileForm.finalRating} />
                    )}
                    {feedbackForm.questions.status && (
                        <ScreeningQuestion data={feedbackForm.questions.data} image={profileForm?.image} />
                    )}
                    <Footer data={profileForm} />
                </>
            ) : (
                <Other page="404Page" />
            )}
        </div>
    );
}

export default FeedbackRoom;
